/* istanbul ignore file - Auth scenarios will be handled in integration testing */
import React, { useEffect } from 'react';
import {
  MsalAuthenticationResult,
  MsalAuthenticationTemplate,
  MsalProvider,
} from '@azure/msal-react';
import {
  AuthenticationResult,
  ClientAuthError,
  EventType,
  InteractionRequiredAuthError,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';

import { CenterLoader } from '@im/ui-lib/CenterLoader';
import { ErrorPage } from '@im/ui-lib/ErrorPage';
import { AuthRequest, DefaultAuthInteractionType } from './AuthRequest';

export const msalInstance = new PublicClientApplication({
  auth: {
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_MSAL_TENANT_ID}`,
    clientId: process.env.REACT_APP_MSAL_CLIENT_ID as string,
    postLogoutRedirectUri: '/',
    redirectUri: '/',
  },
});

const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0]);
}
msalInstance.addEventCallback((event) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS ||
    (event.eventType === EventType.SSO_SILENT_SUCCESS && event.payload)
  ) {
    const account = (event.payload as AuthenticationResult).account;
    if (account) {
      msalInstance.setActiveAccount(account);
    }
  }
});

const LoadingComponent = () => <CenterLoader />;

const ErrorComponent = (props: MsalAuthenticationResult) => {
  const { error, login } = props;

  useEffect(() => {
    if (error instanceof InteractionRequiredAuthError) {
      login(DefaultAuthInteractionType, AuthRequest);
    } else if (
      error instanceof ClientAuthError &&
      error.errorCode === 'state_not_found'
    ) {
      window.location.reload();
    }
  }, [error, login]);

  return (
    <ErrorPage
      errorCode={error?.errorCode}
      errorText={error?.errorMessage}
      showHomeButton={true}
    />
  );
};

export const AuthProvider = (props: { children: React.ReactNode }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        authenticationRequest={AuthRequest}
        errorComponent={ErrorComponent}
        interactionType={InteractionType.Silent}
        loadingComponent={LoadingComponent}
      >
        {props.children}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};
